// react-table depends on spreading props; we could name them explicitly but this component might
// break if the shape of the return value from row.getRowProps() changes
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Cell, Row } from 'react-table';
import cx from 'classnames';

import { IconCaret2 } from '@trqk/components';
import { locale, formatNumberCompact } from '@trqk/utils';

import {
    caretIconWidth,
    musicFileIconWidth,
    summarizeRowsAbove,
} from '../../constants';
import { WorkNameCell } from '.';

interface GroupedWorkNameCellProps {
    cell: Cell<TrqkGapChannelAudio, GapWorkNameCellValue>;
    column: StatementWorkColumn;
    row: Row<TrqkGapChannelAudio>;
}

const { GROUPED_WORK_NAME } =
    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.CONFIRM_WORKS.CELLS;

/**
 * For summary rows, renders a description like "3 Detected Audio Files". A caret icon is shown to
 * the left and the description can be clicked to expand / collapse the row.
 * For non-summary rows, returns a <WorkNameCell />.
 * @param props - {@link GroupedWorkNameCellProps}
 * @returns JSX
 */
export function GroupedWorkNameCell({
    cell,
    column,
    row,
}: GroupedWorkNameCellProps): JSX.Element {
    const width = (column?.width as number) ?? 100;
    const fileOrFiles =
        row.subRows.length === 1
            ? GROUPED_WORK_NAME.FILE
            : GROUPED_WORK_NAME.FILES;
    const { style: expandedRowStyle, ...restOfExpandedRowProps } =
        row.getToggleRowExpandedProps();
    const audioFilesCount = formatNumberCompact(row.subRows.length);

    if (row.subRows.length > summarizeRowsAbove) {
        return (
            <div
                className="grid justify-items-start items-center"
                style={{
                    ...expandedRowStyle,
                    width,
                    gridTemplateColumns: `${musicFileIconWidth}px auto`,
                }}
                {...restOfExpandedRowProps}
            >
                <IconCaret2
                    addClasses="justify-self-center"
                    width={caretIconWidth}
                    rotation={row.isExpanded ? 0 : 270}
                />
                <p
                    className={cx('px-1 font-medium', {
                        'text-xs': audioFilesCount.length > 2,
                        'text-sm': audioFilesCount.length <= 2,
                    })}
                >{`${audioFilesCount} ${GROUPED_WORK_NAME.DETECTED_AUDIO} ${fileOrFiles}`}</p>
            </div>
        );
    }

    return <WorkNameCell cell={cell} column={column} />;
}
